import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { RequireAuth } from './components/layout/RequireAuth';
import { AppLayout } from './components/layout/AppLayout';
import IconSprite from './components/IconSprite';

const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ChangePasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage'));
const SetupWizardPage = lazy(() => import('./pages/setup/SetupWizardPage'));
const ShopSettingsPage = lazy(() => import('./pages/settings/ShopSettingsPage'));
const ProductsPage = lazy(() => import('./pages/products/ProductsPage'));
const ServicesPage = lazy(() => import('./pages/services/ServicesPage'));
const InventoryPage = lazy(() => import('./pages/inventory/InventoryPage'));
const VendorsPage = lazy(() => import('./pages/vendors/VendorsPage'));
const SalesPage = lazy(() => import('./pages/sales/SalesPage'));
const CustomersPage = lazy(() => import('./pages/customers/CustomersPage'));
const ExchangePage = lazy(() => import('./pages/exchange/ExchangePage'));
const RefundPage = lazy(() => import('./pages/refund/RefundPage'));
const HirePurchasePage = lazy(() => import('./pages/hire-purchase/HirePurchasePage'));
const SupportPage = lazy(() => import('./pages/support/SupportPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const EmiProcessingPage = lazy(() => import('./pages/emi-processing/EmiProcessingPage'));
const HaierOrdersPage = lazy(() => import('./pages/haier-orders/HaierOrdersPage'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function WithLayout({ children }: { children: React.ReactNode }) {
  return <AppLayout>{children}</AppLayout>;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <IconSprite />
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected — no layout (full-screen) */}
        <Route element={<RequireAuth />}>
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/setup" element={<SetupWizardPage />} />
        </Route>

        {/* Protected — with sidebar layout */}
        <Route element={<RequireAuth />}>
          <Route
            path="/dashboard"
            element={<WithLayout><DashboardPage /></WithLayout>}
          />
          <Route
            path="/settings"
            element={<WithLayout><ShopSettingsPage /></WithLayout>}
          />
          <Route path="/pos" element={<WithLayout><SalesPage /></WithLayout>} />
          <Route path="/exchange" element={<WithLayout><ExchangePage /></WithLayout>} />
          <Route path="/refund" element={<WithLayout><RefundPage /></WithLayout>} />
          <Route path="/products" element={<WithLayout><ProductsPage /></WithLayout>} />
          <Route path="/emi-processing" element={<WithLayout><EmiProcessingPage /></WithLayout>} />
          <Route path="/services" element={<WithLayout><ServicesPage /></WithLayout>} />
          <Route path="/inventory" element={<WithLayout><InventoryPage /></WithLayout>} />
          <Route path="/customers" element={<WithLayout><CustomersPage /></WithLayout>} />
          <Route path="/vendors" element={<WithLayout><VendorsPage /></WithLayout>} />
          <Route path="/hire-purchase" element={<WithLayout><HirePurchasePage /></WithLayout>} />
          <Route path="/support" element={<WithLayout><SupportPage /></WithLayout>} />
          <Route path="/haier-orders" element={<WithLayout><HaierOrdersPage /></WithLayout>} />
          <Route path="/reports" element={<WithLayout><ReportsPage /></WithLayout>} />
          <Route path="/profile" element={<WithLayout><ProfilePage /></WithLayout>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
